import { useState } from 'react';

interface SummarizationResult {
  summary: string;
  keyPoints: string[];
  sessionName: string;
  fallback?: boolean;
}

interface SummarizationError {
  message: string;
}

export const useLLMGoalSummarizer = () => {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<SummarizationResult | null>(null);
  const [error, setError] = useState<SummarizationError | null>(null);

  const summarize = async (goal: string, context?: any): Promise<SummarizationResult | null> => {
    if (!goal.trim()) {
      setError({ message: 'Goal cannot be empty' });
      return null;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      const baseUrl = import.meta.env.VITE_ARCOS_API_URL || 'http://localhost:8001';
      const response = await fetch(`${baseUrl}/api/llm/summarize-goal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ goal, context })
      });

      if (!response.ok) {
        throw new Error(`API error: ${response.status}`);
      }

      const data = await response.json();
      
      if (!data.success) {
        throw new Error(data.message || 'Summarization failed');
      }

      const summarizationResult: SummarizationResult = {
        summary: data.summary,
        keyPoints: data.keyPoints,
        sessionName: data.sessionName,
        fallback: data.fallback
      };

      setResult(summarizationResult);
      return summarizationResult;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Summarization failed, please try again.';
      setError({ message: errorMessage });
      return null;
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setResult(null);
    setError(null);
    setLoading(false);
  };

  return { loading, result, error, summarize, reset };
};
